import React from 'react';
import { lang } from '@umu/ukit-lang';
import {
    CAPABILITY_TASK_TYPE,
    DATA_TYPE,
    IMemberDataInfo,
    IPracticeHistoryDetailResInfo,
    IPracticeScore,
    SCORE_TYPE,
} from '../interface';
import type { IChartProps } from 'bizcharts/lib/interface';
import type { ILegend } from 'bizcharts/lib/components/Legend';
import type { ITooltip } from 'bizcharts/lib/components/Tooltip';
import type { IAreaGemoProps } from 'bizcharts/lib/geometry/Area';
import type { ILineGemoProps } from 'bizcharts/lib/geometry/Line';
import type { IPointGemoProps } from 'bizcharts/lib/geometry/Point';
import type { IIntervalGemoProps } from 'bizcharts/lib/geometry/Interval';
import { ProgressBarWithMarkers } from '../../progressBarWithMarkers';
import { MarkType, MarkLocation } from '../../progressBarWithMarkers/interface';
import { ChartColorType, ICapabilityAnalysisData } from '../../../components/chartDataView/interface';

const COLOR_MAP = {
    [SCORE_TYPE.LOW]: '#DD4E40',
    [SCORE_TYPE.EQUAL]: '#1E6EE6',
    [SCORE_TYPE.HIGH]: '#21A564',
};
const FULL_SCORE = 100; // 满分
const taskTypeTipList: string[] = [
    lang('- 任务：包括带教导师通过带教任务给出的点评和点评者通过点评任务给出的点评'),
    lang('- 非任务：非带教任务或点评任务时讲师给出的点评'),
];

const defaultDepartmentMemberInfo: IMemberDataInfo = {
    page_info: {
        current_page: 1,
        size: 10,
        list_total_num: 0,
        total_page_num: 1,
    },
    list: [],
    table_head: [],
};
const capabilityTaskOptions = [
    {
        label: lang('全部点评类型'),
        value: CAPABILITY_TASK_TYPE.ALL,
    },
    {
        label: lang('任务_Tasks'),
        value: CAPABILITY_TASK_TYPE.TASK,
    },
    {
        label: lang('非任务_Non_tasks'),
        value: CAPABILITY_TASK_TYPE.NO_TASK,
    },
];

const defaultPracticeHistoryResInfo: IPracticeHistoryDetailResInfo = {
    table_head: [],
    list: [],
};

const legendList = [
    {
        id: SCORE_TYPE.LOW,
        label: lang('低于平均值'),
        color: COLOR_MAP[SCORE_TYPE.LOW],
    },
    {
        id: SCORE_TYPE.EQUAL,
        label: lang('等于平均值'),
        color: COLOR_MAP[SCORE_TYPE.EQUAL],
    },
    {
        id: SCORE_TYPE.HIGH,
        label: lang('高于平均值'),
        color: COLOR_MAP[SCORE_TYPE.HIGH],
    },
];

/**
 * @desc 展示该学员在各个评价标准的得分及其与该企业在平台内该评价标准得分平均值的对比情况。
 *       其中分数等于平均值的展示为蓝色，高于平均值的展示为绿色，低于平均值的展示为红色。
 */
const getPracticeScoreColor = (scoreInfo: IPracticeScore) => {
    const { average_score = 0, score = 0 } = scoreInfo || {};
    if (score === average_score) {
        return COLOR_MAP[SCORE_TYPE.EQUAL];
    } else if (score > average_score) {
        return COLOR_MAP[SCORE_TYPE.HIGH];
    } else {
        return COLOR_MAP[SCORE_TYPE.LOW];
    }
};
const onRenderPracticeScore = (scoreInfo?: IPracticeScore) => {
    if (!scoreInfo?.score) {
        return '-';
    }

    const color = getPracticeScoreColor(scoreInfo);

    const { total_score = 0 } = scoreInfo || {};
    const score = Math.min(scoreInfo?.score || 0, total_score);
    const averageScore = Math.min(scoreInfo?.average_score || 0, total_score);

    const markers = [
        {
            position: averageScore,
            label: `${averageScore} ${lang('(平均分)')}`,
            color: '#FBB70E',
            type: MarkType.TRIANGLE,
            location: MarkLocation.TOP,
        },
        { position: score, label: `${score}`, color: color, type: MarkType.TRIANGLE, location: MarkLocation.BOTTOM },
        { position: averageScore, color: '#FBB70E', type: MarkType.LINE, location: MarkLocation.BOTTOM },
    ];
    return (
        <ProgressBarWithMarkers
            offsetWidth={40}
            suffixText={`${total_score} (${lang('满分')})`}
            progress={score}
            color={color}
            markers={markers}
        />
    );
};

const getAggregateScoreTooltips = (isAdmin: boolean) => {
    if (!isAdmin) {
        return lang(
            '根据数据视图设置模块的规则，由其组成内容得分及权重计算得出的综合评分，及其与该企业在平台内该视图下得分平均值的对比情况。其中分数等于平均值的展示为蓝色，高于平均值的展示为绿色，低于平均值的展示为红色。',
        );
    }
    const linkUrls = '/platformManage/index#/practice-eval-manage?tab=3';

    const tips = lang(
        '根据<a class="multi-capability-link-text" href="${linkUrls}" target="_blank">数据视图设置</a>模块的规则，由其组成内容得分及权重计算得出的综合评分，及其与该企业在平台内该视图下得分平均值的对比情况。其中分数等于平均值的展示为蓝色，高于平均值的展示为绿色，低于平均值的展示为红色。',
    ).replace('${linkUrls}', linkUrls);
    return (
        <p
            className="multi-capability-aggregate-score-tooltip"
            dangerouslySetInnerHTML={{
                __html: tips,
            }}
        />
    );
};

export const compareChartType = 'type';

export enum CompareChartKeyEnum {
    personal_data = 'personal_data',
    department_data = 'department_data',
    enterprise_mean_comparison_data = 'enterprise_mean_comparison_data',
}

export const CompareChartKeySortMap: { [key: string]: number } = {
    [CompareChartKeyEnum.personal_data]: 1,
    [CompareChartKeyEnum.department_data]: 2,
    [CompareChartKeyEnum.enterprise_mean_comparison_data]: 3,
};

export type CompareChartProps = {
    tooltip?: ITooltip;
    isCompare?: boolean;
    legendProps?: ILegend;
    areaProps?: IAreaGemoProps;
    lineProps?: ILineGemoProps;
    radarColor?: ChartColorType;
    pointProps?: IPointGemoProps;
    radarChartProps?: IChartProps;
    intervalColor?: ChartColorType;
    intervalChartProps?: IChartProps;
    intervalProps?: IIntervalGemoProps;
};

export interface GetTooltipTplParams extends ICapabilityAnalysisData {
    isOnlyUMU?: boolean;
    isCompare?: boolean;
}

export interface CompareChartDataType {
    title: string;
    color: string;
}

export const getCompareFirstKey = (pageType: number): string => {
    if (pageType === DATA_TYPE.USER) {
        return CompareChartKeyEnum.personal_data;
    }

    return CompareChartKeyEnum.department_data;
};

export const getCompareChartDataMap = (pageType: number, isDetail = false): { [key: string]: CompareChartDataType } => {
    if (pageType === DATA_TYPE.USER) {
        return {
            [CompareChartKeyEnum.personal_data]: {
                color: isDetail ? '#2197F3' : '#1E6EE6',
                title: lang('个人数据'),
            },
            [CompareChartKeyEnum.enterprise_mean_comparison_data]: {
                color: '#FAB400',
                title: lang('企业平均值对比数据'),
            },
        };
    }

    return {
        [CompareChartKeyEnum.department_data]: {
            color: isDetail ? '#2197F3' : '#1E6EE6',
            title: lang('部门数据'),
        },
        [CompareChartKeyEnum.enterprise_mean_comparison_data]: {
            color: '#FAB400',
            title: lang('企业平均值对比数据'),
        },
    };
};

export const transformChartCompareData = (pageType: number, data: ICapabilityAnalysisData[], isDetail = false) => {
    const newData: ICapabilityAnalysisData[] = [];
    const compareChartDataMap = getCompareChartDataMap(pageType, isDetail);

    Object.keys(compareChartDataMap).forEach((item1) => {
        data.forEach((item2) => {
            newData.push({
                ...item2,
                [compareChartType]: item1,
                value: item2.info?.[item1] || 0,
            });
        });
    });

    return newData;
};

export const sortRadarData = (data: ICapabilityAnalysisData[]) => {
    if (!data) {
        return [];
    }

    return [...data].sort((a, b) => {
        return (
            CompareChartKeySortMap[b[compareChartType] || CompareChartKeyEnum.personal_data] -
            CompareChartKeySortMap[a[compareChartType] || CompareChartKeyEnum.personal_data]
        );
    });
};

const normalizedCompareChartColor = (compareChartDataMap: { [key: string]: CompareChartDataType }): ChartColorType => {
    return [
        compareChartType,
        (pageType: string) => {
            return compareChartDataMap[pageType]?.color;
        },
    ];
};

export const normalizedCompareChartProps = (
    pageType: number,
    chartProps: CompareChartProps,
    isDetail = false,
): CompareChartProps => {
    const { radarChartProps, intervalChartProps } = chartProps;
    const compareChartDataMap = getCompareChartDataMap(pageType, isDetail);
    const color = normalizedCompareChartColor(compareChartDataMap);

    return {
        isCompare: true,
        tooltip: {
            shared: false,
        },
        radarChartProps: {
            theme: {},
            ...radarChartProps,
        },
        intervalChartProps: {
            theme: {},
            ...intervalChartProps,
        },
        radarColor: color,
        intervalColor: color,
        legendProps: {
            custom: true,
            position: 'top',
            allowAllCanceled: true,
            items: Object.keys(compareChartDataMap).map((key) => ({
                value: key,
                name: compareChartDataMap[key].title,
                marker: {
                    symbol: 'circle',
                    style: { fill: compareChartDataMap[key].color, r: 5 },
                },
            })),
        },
        intervalProps: {
            adjust: [
                {
                    type: 'dodge',
                    marginRatio: 0,
                },
            ],
        } as IIntervalGemoProps,
        lineProps: {
            color,
            size: 3,
            tooltip: false,
        } as ILineGemoProps,
        pointProps: {
            color,
            size: 3,
        } as IPointGemoProps,
        areaProps: {
            color,
            tooltip: false,
            style: {
                opacity: 1,
                lineWidth: 1,
                fillOpacity: 0.2,
                strokeOpacity: 1,
            },
        } as unknown as IAreaGemoProps,
    };
};

export const getTooltipTplOfPracticeDataChart = (dataSource: GetTooltipTplParams) => {
    const { type, isOnlyUMU, isCompare } = dataSource;

    if (isCompare && type === CompareChartKeyEnum.enterprise_mean_comparison_data) {
        return [
            {
                label: lang('得分'),
                count: dataSource.value,
            },
        ];
    }

    if (isOnlyUMU) {
        return [
            {
                label: lang('得分'),
                count: dataSource.value,
            },
            {
                label: lang('作业数量'),
                count: dataSource?.info?.homework_num || 0,
            },
        ];
    }

    return [
        {
            label: lang('得分'),
            count: dataSource.value,
        },
        {
            label: lang('练习次数'),
            count: dataSource?.info?.total_practice_num || 0,
        },
        {
            indent: true,
            label: lang('提交作业数'),
            count: dataSource?.info?.homework_num || 0,
        },
        {
            indent: true,
            label: lang('练习频道视频数'),
            count: dataSource?.info?.homework_practice_num || 0,
        },
        {
            indent: true,
            label: lang('Chatbot提交数'),
            count: dataSource?.info?.chatbot_practice_num || 0,
        },
    ];
};

export const getTooltipTplOfPracticeView = (dataSource: GetTooltipTplParams) => {
    const { type, isOnlyUMU, isCompare } = dataSource;

    if (isCompare && type === CompareChartKeyEnum.enterprise_mean_comparison_data) {
        return [
            {
                label: lang('得分'),
                count: dataSource.value,
            },
        ];
    }

    if (isOnlyUMU) {
        return [
            {
                label: lang('得分'),
                count: dataSource.value,
            },
            {
                label: lang('作业数量'),
                count: dataSource?.info?.homework_num || 0,
            },
        ];
    }

    return [
        {
            label: lang('得分'),
            count: dataSource.value,
        },
        {
            label: lang('练习次数'),
            count: dataSource?.info?.total_practice_num || 0,
        },
        {
            indent: true,
            label: lang('提交作业数'),
            count: dataSource?.info?.homework_num || 0,
        },
        {
            indent: true,
            label: lang('练习频道视频数'),
            count: dataSource?.info?.homework_practice_num || 0,
        },
        {
            indent: true,
            label: lang('Chatbot提交数'),
            count: dataSource?.info?.chatbot_practice_num || 0,
        },
    ];
};

export {
    legendList,
    defaultDepartmentMemberInfo,
    defaultPracticeHistoryResInfo,
    FULL_SCORE,
    taskTypeTipList,
    capabilityTaskOptions,
    onRenderPracticeScore,
    getPracticeScoreColor,
    getAggregateScoreTooltips,
};
